import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { defaultFlags } from '../config/defaultFeatureFlags';
import { getQueryOverrides } from '../utils/featureFlagUtil';

const STORAGE_KEY = 'feature_flags_store';

const FeatureFlagContext = createContext({
  featureFlags: defaultFlags,
  isLoading: true,
  isFeatureEnabled: () => false,
  refresh: () => {},
});

function getEnvironment() {
  const rawEnv = process.env.REACT_APP_ENVIROMENT || 'green_dev';

  const envMap = {
    green_dev: 'development',
    prod: 'production',
  };

  return envMap[rawEnv] || 'development'; // fallback to development if unrecognized
}

function getFlagsUrl(env) {
  return `/featureFlags.${env}.json`;
}

function loadCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveCache(featureFlags) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(featureFlags));
  } catch {
    // localStorage unavailable (private mode, quota, etc.) — safe to ignore
  }
}

/**
 * FeatureFlagProvider
 *
 * @param {string} [featureFlagsUrl] - path to a JSON file, e.g. in
 *   CRA's `public/` folder it's served at the site root: public/featureFlags.${env}.json -> "/featureFlags.${env}.json"
 * @param {number} [refetchInterval=0] - re-fetch interval; 0 = fetch once on load (typical for a static file), unit = Milliseconds 
 * @param {object} [overrides] - force specific featureFlags in code
 * @param {boolean} [allowQueryOverrides=true] - let ?feature_flagName=value in the URL override featureFlags at runtime
 */
export function FeatureFlagProvider({
  children,
  featureFlagsUrl,
  refetchInterval = 0,
  overrides = {},
  allowQueryOverrides = true,
}) {
  const environment = getEnvironment();
  
  const resolvedUrl = featureFlagsUrl || getFlagsUrl(environment);
  const [rawFlags, setRawFlags] = useState(() => loadCache() ?? defaultFlags);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef(null);

  const queryOverrides = allowQueryOverrides ? getQueryOverrides() : {};

  const fetchFeatureFlags = async () => {
    try {
      // cache: 'no-store' avoids the browser serving a stale copy of the
      // static JSON file after you've deployed an update.
      const response = await fetch(resolvedUrl, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Flag fetch failed: ${response.status}`);
      const featureData = await response.json();
      setRawFlags(featureData);
      saveCache(featureData);
    } catch (err) {
      console.warn('[feature-flags] using cached/default flags:', err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatureFlags();
    if (refetchInterval > 0) {
      intervalRef.current = setInterval(fetchFeatureFlags, refetchInterval);
    }
    return () => clearInterval(intervalRef.current);
  }, [resolvedUrl, refetchInterval]);

  const resolveFlag = (key) => {
    // Priority: query param override > code override > remote/file value > default
    if (key in queryOverrides) return !!queryOverrides[key];
    if (key in overrides) return !!overrides[key];

    const value = rawFlags[key] ?? defaultFlags[key];
    return !!value;
  };

  const isFeatureEnabled = (key) => resolveFlag(key);

  const allKeys = new Set([
    ...Object.keys(defaultFlags),
    ...Object.keys(rawFlags),
    ...Object.keys(overrides),
    ...Object.keys(queryOverrides),
  ]);

  const resolvedFlags = Array.from(allKeys).reduce(
    (acc, key) => ({ ...acc, [key]: resolveFlag(key) }),
    {}
  );

  return (
    <FeatureFlagContext.Provider
      value={{ featureFlags: resolvedFlags, isLoading, isFeatureEnabled, refresh: fetchFeatureFlags }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
}

/** Hook: get a single flag's resolved boolean value */
export function useFeatureFlag(key) {
  const { isFeatureEnabled } = useContext(FeatureFlagContext);
  return isFeatureEnabled(key);
}

/** Hook: get everything — all feature flags, loading state, manual refresh */
export function useFeatureFlags() {
  return useContext(FeatureFlagContext);
}